<script lang="ts">
  import Portrait from '../../components/portrait.svelte';
  import { createDie } from '../../models/die.type';
  import type { Die as DieObject, DieType } from '../../models/die.type';
  import Die from '../../dice-tray/die.svelte';
  import { glossary } from '../../../store';
  import type { PlayerDefinition, PlayerArchetype } from '../../models/player.type';

  interface Props {
    data: PlayerDefinition;
  }

  let { data }: Props = $props();

  /**
   * dice1/dice2/dice3 are three parallel arrays indexed by archetype, not three
   * loadouts -- dice1[2], dice2[2] and dice3[2] are the one weapon, item and
   * ability that the third archetype gets. The old layout rendered them as three
   * flat rows, which showed every die a class can field but gave no way to see
   * which hero any of them belonged to. This regroups them by archetype, which
   * is the same shape glossary.svelte builds its Heroes list from.
   */
  const SLOTS = ['Weapon', 'Item', 'Ability'];

  type Loadout = {
    archetype: PlayerArchetype;
    slots: Array<{ label: string; die: DieObject }>;
  };

  let loadouts: Array<Loadout> = $state.raw([]);

  if (data) {
    loadouts = data.archetypes.map((archetype, index) => {
      // The three dice lists are optional on PlayerDefinition, and nothing
      // guarantees they run as long as the archetype list.
      const picks: Array<DieType | undefined> = [
        data.dice1?.[index],
        data.dice2?.[index],
        data.dice3?.[index],
      ];

      const slots: Array<{ label: string; die: DieObject }> = [];
      picks.forEach((type, slot) => {
        if (!type) {
          return;
        }
        // Unique per class/archetype/slot. The old ids were 'p' + index, which
        // collided across the three lists -- every archetype's weapon, item and
        // ability shared one id.
        const id = `${data.type}-${archetype.id}-${slot}`;
        slots.push({ label: SLOTS[slot], die: createDie(type, slot, id) });
      });

      return { archetype, slots };
    });
  }

  /**
   * Jumps to this archetype's entry in the Heroes section. Order matters:
   * selecting a category clears the search term, so the term has to be set
   * after it. Archetype titles are unique across all four classes, so the term
   * narrows Heroes down to exactly this one.
   */
  function openHero(archetype: PlayerArchetype) {
    glossary.selected.set('heroes');
    glossary.searchTerm.set(archetype.title);
  }
</script>

<!-- data-class-entry is what a hero's back-link scrolls to. -->
<div
  class="container entry"
  data-class-entry={data.type}
  style="--class-colour: {data.colour ?? '#87442b'}"
>
  <div class="overview">
    <div class="class-portrait">
      <Portrait {data}></Portrait>
    </div>

    <div class="blurb">
      <h2>{data.title}</h2>
      <p>{data.text}</p>

      <div class="stats">
        <span class="stat"><b>{data.healthMax}</b> Health</span>
        {#if data.rerollMax}
          <span class="stat"><b>{data.rerollMax}</b> Rerolls</span>
        {/if}
        <span class="stat"><b>{loadouts.length}</b> Heroes</span>
      </div>
    </div>
  </div>

  <div class="archetypes">
    {#each loadouts as loadout (loadout.archetype.id)}
      <div class="archetype">
        <button class="hero" onclick={() => openHero(loadout.archetype)}>
          <div class="hero-portrait">
            <Portrait data={{ type: loadout.archetype.id }}></Portrait>
          </div>

          <span class="name">{loadout.archetype.title}</span>
          <span class="complexity {loadout.archetype.complexity.toLowerCase()}">
            {loadout.archetype.complexity}
          </span>
        </button>

        <div class="loadout">
          {#each loadout.slots as slot (slot.die.id)}
            <div class="slot">
              <span class="label">{slot.label}</span>
              <div class="die-container">
                <Die die={slot.die} die3D={false} hover={true} sizeMod={1} />
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  // No card. The section is one document, so the entry has no edges of its own
  // -- the class' colour rides on the frames around its portraits instead, via
  // --portrait-accent in components/portrait.svelte. Padding and the rule that
  // separates one entry from the next come from .entry in glossary.svelte.
  .container {
    text-align: left;
    --portrait-accent: var(--class-colour);
  }

  .overview {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 16px;
    align-items: start;
    margin-bottom: 16px;

    @media @mobile {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      text-align: center;
    }
  }

  .class-portrait {
    // Portrait sizes itself off --column-size, which is 300px app-wide so the
    // battle screen can use it. Far too big for a reference entry.
    --column-size: 150px;

    @media @mobile {
      --column-size: 120px;
    }
  }

  .blurb {
    h2 {
      font-size: 1.5rem;
      text-transform: capitalize;
      color: var(--class-colour);
      margin-bottom: 6px;
    }

    p {
      font-size: 0.9rem;
      line-height: 1.5;
      opacity: 0.85;
    }
  }

  .stats {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 8px;
    margin-top: 12px;

    @media @mobile {
      justify-content: center;
    }

    // A stat line rather than a row of pills: the pills were the most
    // dashboard-like thing on the page, and these are three numbers, not three
    // filters you can press.
    .stat {
      padding-right: 14px;
      font-size: 0.75rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.7;
      border-right: 1px solid var(--background-colour-light);

      &:last-child {
        padding-right: 0;
        border-right: none;
      }

      b {
        color: var(--class-colour);
        font-size: 0.95rem;
        letter-spacing: 0;
      }
    }
  }

  .archetypes {
    // auto-fit rather than a fixed count: four heroes sit in a row on the wide
    // panel and fold to two, then one, as the column narrows -- no breakpoints,
    // and it still works if a class is ever given a fifth archetype.
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
    grid-gap: 10px;
  }

  .archetype {
    display: flex;
    flex-direction: column;
    // No overflow clipping anywhere in here: the die tooltips are ~335px wide
    // and absolutely positioned, so they have to be free to escape the column.
  }

  .hero {
    // Reset of the global button.less pill.
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-gap: 6px;
    width: 100%;
    padding: 10px 8px 6px;
    background: none;
    box-shadow: none;
    border-radius: 6px;
    transition: background 0.2s;

    &:hover,
    &:focus-visible {
      filter: none;
      background: rgba(255, 255, 255, 0.06);

      .name {
        color: var(--class-colour);
      }
      .name::after {
        transform: translateX(3px);
      }
    }

    &:active {
      box-shadow: none;
    }

    .name {
      font-size: 0.95rem;
      font-weight: 700;
      transition: color 0.2s;

      // Marks the card as a way through to the Heroes section.
      &::after {
        content: ' \203A';
        display: inline-block;
        transition: transform 0.2s;
      }
    }

    .complexity {
      padding: 2px 10px;
      font-size: 0.65rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      border-radius: 999px;
      border: 1px solid currentColor;
      opacity: 0.8;

      &.simple {
        color: var(--green);
      }
      &.moderate {
        color: var(--yellow);
      }
      &.complex {
        color: var(--orange);
      }
    }
  }

  .hero-portrait {
    --column-size: 90px;
    pointer-events: none;
  }

  .loadout {
    display: flex;
    flex-direction: column;
    grid-gap: 4px;
    padding: 4px 10px 0;

    // Label and die packed together and centred under their hero, with the
    // label column held at a fixed width so the three dice still line up. Left
    // to stretch across the full column, each label sat closer to the next
    // hero's dice than to its own once the columns lost their edges.
    .slot {
      display: grid;
      grid-template-columns: 58px auto;
      justify-content: center;
      grid-gap: 8px;
      align-items: center;

      .label {
        font-size: 0.65rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        text-align: right;
        opacity: 0.55;
      }

      .die-container {
        position: relative;
      }
    }
  }
</style>
